import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction } from "@fluentui/react";

import { memoizeLastResultPerId } from "@1js/acui-memoization";

import type { RibbonControlGroupProps } from ".";
import { RibbonControlGroup } from ".";
import type {
  RibbonControlInternalProps,
  RibbonControlProps,
} from "../../Controls/RibbonControlProps";

/**
 * Get the controls (visible, not overflow, controls in SLR)
 * for a give RibbonControlGroup
 * @param props the props for a RibbonControlGroup
 * @returns an array of the controls
 */
const getControlGroupControls = (props: RibbonControlGroupProps) => {
  switch (props.type) {
    case "SingleLine":
      return props.topLevelControls;
    case "Multiline":
      return props.controls;
  }
};

/**
 * Check if all control for the given controlProps are disabled.
 * Note: this MUST only be used when the controls are not connected to
 * the store!
 * @param controlProps list of controlProps to check
 * @returns true of all controls are disabled, false otherwise
 */
const allControlsDisabled = (controlProps?: RibbonControlProps[]): boolean => {
  if (!controlProps) {
    return true;
  }

  // Return true only if none of the sets of controlProps
  // has disabled set to true
  return !controlProps.some((props: RibbonControlProps) => {
    // Need to cast to internal props to have access to the "store only" props
    const internalProps = props as RibbonControlInternalProps;
    return (
      !internalProps.disabled &&
      (internalProps.type !== "AppSplitButtonProps" ||
        !internalProps.primaryDisabled)
    );
  });
};

/**
 * Gets a fast disable-able RibbonControlGroup that
 * utilizes inherited disabled controlProps (e.g. controls not connected to the store)
 * to determine if it should fast disable
 */
export const getFastDisableableInheritedPropsRibbonControlGroupRenderer =
  memoizeFunction(() => {
    return (props: RibbonControlGroupProps) => {
      return (
        <RibbonControlGroup
          {...props}
          fastDisabled={
            props.fastDisabled !== undefined
              ? props.fastDisabled
              : allControlsDisabled(getControlGroupControls(props))
          }
        />
      );
    };
  });

const shouldShadowRenderMemoizeReplacer = (_: string, value: any) => {
  // use a toString of the function since you can't stringify a function
  // and we will be using the same function for the same inputs
  if (typeof value === "function") {
    return value.toString();
  }
  return value;
};

export const shouldShadowRenderMenu = memoizeLastResultPerId(
  (
    _idAndLocation: string,
    fastDisabled?: boolean,
    shadowRenderMenu?: boolean
  ) => {
    return (): boolean | undefined => {
      return !fastDisabled && shadowRenderMenu;
    };
  },
  shouldShadowRenderMemoizeReplacer
);
